import Phaser from 'phaser';
import { ScriptedDice, applyInput } from '@casino/craps-engine';
import type { CrapsSnapshot, Engine, EngineInput, TableRules } from '@casino/craps-engine';
import { createCrapsBridge } from '../bridge/crapsBridge.js';
import { BootScene as CrapsBootScene } from '../scenes/craps/BootScene.js';
import { DiceOverlayScene as CrapsDiceOverlayScene } from '../scenes/craps/DiceOverlayScene.js';
import { TableScene as CrapsTableScene } from '../scenes/craps/TableScene.js';
import { UIScene as CrapsUIScene } from '../scenes/craps/UIScene.js';
import { initOrientation } from '../orientation.js';
import { mountSpectatorDebugOverlay } from './debugOverlay.js';
import { createNarrationBanner, noteFromEvent } from './narrationBanner.js';
import { createWatchController } from './watchController.js';

type BannerMode = 'live' | 'replay' | null;
type BannerSetter = (mode: BannerMode) => void;

/** Minimal header `bootSpectatorScene` needs to construct the craps scene.
 *  The keyframe carries bankroll/bets/phase, so the header only describes the
 *  table identity + game + a live/ended banner mode. */
interface SpectatorHeader {
  game: string;
  table_key: string;
  rules: TableRules;
  status: 'live' | 'ended';
}

/** Watch a live producer by stream_code. Connects to the per-user StreamDO,
 *  drives a `createWatchController`, and (re)hydrates a Phaser craps scene from
 *  the keyframes/events the DO relays. No D1, no session catchup — the DO
 *  buffers the current shooter-turn and re-sends catchup/offline on every
 *  connect, so reconnect is just "open a fresh socket." */
export async function bootWatchingMode(streamCode: string): Promise<void> {
  const dbg = mountSpectatorDebugOverlay();
  const narration = createNarrationBanner();

  // Bridge engine + dice, populated once the scene boots. While the scene is
  // spinning up, incoming events are buffered and drained after boot.
  let engine: Engine | null = null;
  let dice: ScriptedDice | null = null;
  let setBanner: BannerSetter | null = null;
  let bootingScene = false;
  let bootBuffer: EngineInput[] = [];
  let offlineEl: HTMLElement | null = null;

  const clearOfflineScreen = (): void => {
    if (offlineEl) {
      offlineEl.remove();
      offlineEl = null;
    }
  };

  const applyEvent = (input: EngineInput): void => {
    if (bootingScene || !engine || !dice) {
      bootBuffer.push(input);
      return;
    }
    applyInput(engine, dice, input);
  };

  const controller = createWatchController({
    onOffline: () => {
      dbg.setPhase('ended');
      void renderOfflineScreen(streamCode).then((el) => {
        // Only mount if we haven't booted/started booting in the meantime.
        if (engine || bootingScene) {
          el.remove();
          return;
        }
        clearOfflineScreen();
        offlineEl = el;
        document.body.appendChild(el);
      });
    },

    onBoot: ({ game, tableKey, rules, keyframe }) => {
      clearOfflineScreen();
      if (game !== 'craps') {
        showMessage(`Watching game type "${game}" is not supported yet.`);
        return;
      }
      bootingScene = true;
      dbg.setPhase('booting');
      const header: SpectatorHeader = {
        game,
        table_key: tableKey,
        rules: rules as TableRules,
        status: 'live',
      };
      void bootSpectatorScene(header, keyframe as CrapsSnapshot).then((res) => {
        engine = res.engine;
        dice = res.dice;
        setBanner = res.setBanner;
        // Drain events that arrived while Phaser was booting.
        for (const input of bootBuffer) applyInput(res.engine, res.dice, input);
        bootBuffer = [];
        bootingScene = false;
        setBanner('live');
        dbg.setPhase('live');
      });
    },

    onApply: (event) => {
      const note = noteFromEvent(event);
      if (note) narration.show(note);
      applyEvent(event as unknown as EngineInput);
    },

    onReset: (keyframe) => {
      // Felt clears (new shooter-turn) or reconnect re-hydrate. If the scene
      // is still booting, the buffered events already include the producer's
      // state up to the keyframe, so re-hydration happens post-boot via the
      // bootBuffer drain — but the controller never emits onReset before the
      // first onBoot, so `engine` is set here in practice.
      if (engine) engine.hydrate(keyframe as CrapsSnapshot);
    },
  });

  // ── WebSocket plumbing ────────────────────────────────────────────────────
  const wsUrl = `${location.origin.replace(/^http/, 'ws')}/api/streams/${encodeURIComponent(streamCode)}/watch`;

  let ws: WebSocket = openWatchSocket();

  function openWatchSocket(): WebSocket {
    const sock = new WebSocket(wsUrl);
    sock.addEventListener('open', () => dbg.pushFrame('ws-open'));
    sock.addEventListener('close', () => dbg.pushFrame('ws-close'));
    sock.addEventListener('error', () => dbg.pushFrame('ws-error'));
    sock.onmessage = (msg: MessageEvent<string>) => {
      // Reject stragglers from a superseded socket so a reconnect race can't
      // double-deliver frames.
      if (sock !== ws) return;
      handleWireFrame(msg.data);
    };
    return sock;
  }

  const handleWireFrame = (data: string): void => {
    let frame: { kind: string; event?: { seq: number; event: { type: string } } };
    try {
      frame = JSON.parse(data);
    } catch {
      return;
    }
    // Debug overlay: surface the frame kind and (for live) the event seq/type.
    if (frame.kind === 'live' && frame.event) {
      dbg.pushFrame('live');
      dbg.pushEvent(frame.event.seq, frame.event.event.type);
    } else {
      dbg.pushFrame(frame.kind);
    }
    controller.handleFrame(frame);
  };

  // Keepalive: send a ping every 25s so idle-connection timers in NATs /
  // proxies don't reap the socket. The DO drops watcher-sent frames, but the
  // bytes keep the connection alive in network terms.
  let keepalive: ReturnType<typeof setInterval> | null = setInterval(() => {
    if (ws.readyState === 1 /* OPEN */) {
      try {
        ws.send(JSON.stringify({ kind: 'ping' }));
      } catch {
        /* ignore */
      }
    }
  }, 25_000);
  void keepalive; // referenced; never cleared (watch sessions run until nav-away)

  // Background-tab recovery. Chrome throttles hidden tabs: setInterval clamps
  // and the WebSocket can be dead at the network level long before onclose
  // fires. On visibilitychange→visible we force a fresh socket if we were
  // hidden a while or the socket isn't OPEN. The DO re-sends catchup/offline
  // on connect, so the controller re-hydrates from the keyframe — no from_seq
  // needed.
  let hiddenAt: number | null = null;
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    const hiddenMs = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = null;
    const wsHealthy = ws.readyState === 1 /* OPEN */;
    if (wsHealthy && hiddenMs < 25_000) return;
    dbg.pushFrame('vis-reconnect', `hidden=${Math.round(hiddenMs / 1000)}s`);
    const old = ws;
    ws = openWatchSocket();
    // Close the old socket *after* swapping so the onmessage zombie guard
    // (sock !== ws) reliably rejects any stragglers.
    try {
      old.close();
    } catch {
      /* ignore */
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
}

/** Fetch presence and build a simple offline/waiting screen element (not yet
 *  mounted). Kept OPEN: when the producer goes live the DO pushes catchup,
 *  onBoot fires, and we clear this screen automatically. */
async function renderOfflineScreen(streamCode: string): Promise<HTMLElement> {
  let displayName: string | null = null;
  try {
    const res = await fetch(`/api/streams/${encodeURIComponent(streamCode)}`);
    if (res.ok) {
      const body = (await res.json()) as { live: boolean; display_name: string | null };
      displayName = body.display_name;
    }
  } catch {
    /* offline screen renders without a name */
  }

  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; inset: 0; z-index: 9000;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 12px; padding: 24px; text-align: center;
    font-family: sans-serif; color: #e8e8e8; background: #0b0f14;
  `;
  const waiting = displayName
    ? `<div style="opacity:0.7">Waiting for ${escapeHtml(displayName)}…</div>`
    : '';
  el.innerHTML = `
    <div style="font-size:20px;font-weight:600">Not currently playing</div>
    ${waiting}
    <a href="/" style="color:#d4af37;text-decoration:none;margin-top:8px">← Back to lobby</a>
  `;
  return el;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Full-screen plain message (e.g. unsupported game type). */
function showMessage(html: string): void {
  document.body.innerHTML = `<div style="padding:24px;font-family:sans-serif">
    ${html} <a href="/">Back to lobby</a>
  </div>`;
}

/**
 * Construct the real Phaser game + craps scenes, then mount the bridge in
 * spectate mode and hydrate it from the producer's keyframe (which carries
 * bankroll/bets/phase/point/history). Returns the bridge engine + dice + a
 * banner setter so the caller can drive live frames and toggle the pill.
 */
async function bootSpectatorScene(
  header: SpectatorHeader,
  keyframe: CrapsSnapshot,
): Promise<{ engine: Engine; dice: ScriptedDice; setBanner: BannerSetter }> {
  // Ensure a fresh #game div exists for Phaser to mount inside. Preserve any
  // other body content (debug overlay, fixed-position chrome) — only touch the
  // #game container itself.
  let gameDiv = document.getElementById('game');
  if (gameDiv) {
    gameDiv.innerHTML = '';
  } else {
    gameDiv = document.createElement('div');
    gameDiv.id = 'game';
    document.body.appendChild(gameDiv);
  }

  const dpr = window.devicePixelRatio || 1;
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    width: Math.round(window.innerWidth * dpr),
    height: Math.round(window.innerHeight * dpr),
    scale: { mode: Phaser.Scale.NONE, autoCenter: Phaser.Scale.NO_CENTER },
    audio: { noAudio: true },
    scene: [CrapsBootScene, CrapsTableScene, CrapsUIScene, CrapsDiceOverlayScene],
  });
  game.registry.set('dpr', dpr);

  // initOrientation pins the canvas's CSS size to its bitmap size / dpr so it
  // fills the viewport instead of overflowing at full bitmap dimensions.
  const parent = document.getElementById('game');
  if (parent) {
    initOrientation({
      game: game as unknown as Parameters<typeof initOrientation>[0]['game'],
      parent,
      getViewport: () => ({ width: window.innerWidth, height: window.innerHeight }),
    });
  }

  // Wait for Phaser to finish creating the scenes. The BootScene starts
  // CrapsTableScene + launches CrapsUIScene + CrapsDiceOverlayScene; we need
  // TableScene + UIScene running before constructing the bridge.
  await waitForGameReady(game);
  await waitForScenesRunning(game, ['CrapsTableScene', 'CrapsUIScene']);

  const tableScene = game.scene.getScene('CrapsTableScene') as CrapsTableScene;
  const uiScene = game.scene.getScene('CrapsUIScene') as CrapsUIScene;
  const tableKey = header.table_key as 'standard' | 'high-limit' | 'whale';
  const bridgeEngine = createCrapsBridge({
    table: tableScene,
    ui: uiScene,
    tableKey,
    mode: 'spectate',
  });
  // Replay the producer's accumulated state (bets/phase/point/history +
  // bankroll) into the bridge engine so the felt + pill match the producer's
  // current shooter-turn. The keyframe embeds bankroll.
  const bridgeDice = new ScriptedDice();
  bridgeEngine.setDiceSource(bridgeDice);
  bridgeEngine.hydrate(keyframe);

  // Mount the Phaser spectator banner under the bankroll bezel.
  uiScene.setSpectatorBanner(header.status === 'ended' ? 'replay' : 'live');
  const setBanner: BannerSetter = (mode) => {
    uiScene.setSpectatorBanner(mode);
  };

  return { engine: bridgeEngine, dice: bridgeDice, setBanner };
}

/** Resolve once Phaser fires its global `ready` event. If the game is already
 *  running by the time we attach, resolve immediately. */
function waitForGameReady(game: Phaser.Game): Promise<void> {
  return new Promise<void>((resolve) => {
    if (game.isRunning) {
      resolve();
      return;
    }
    game.events.once('ready', () => resolve());
  });
}

/** Resolve once each named scene has reached RUNNING status. */
function waitForScenesRunning(game: Phaser.Game, keys: string[]): Promise<void> {
  return new Promise<void>((resolve) => {
    const check = () => {
      const allRunning = keys.every((k) => {
        const scene = game.scene.getScene(k);
        return scene !== null && scene.sys.settings.status === Phaser.Scenes.RUNNING;
      });
      if (allRunning) {
        resolve();
        return;
      }
      requestAnimationFrame(check);
    };
    check();
  });
}
